import React, { useEffect, useState } from "react";
import {
  Car,
  Gauge,
  Settings,
  ShieldAlert,
  Ticket,
  Calendar,
  Clock,
  Mail,
  Phone,
  Building,
  Map,
  MapPin,
} from "lucide-react";
import { camelToNormal } from "../../common/caseconversion";
import formatDateAndTime from "../../common/formatDateAndTime";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Typography from "@mui/material/Typography";
import { capsconversion } from "../../common/capsconversion";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import toast from "react-hot-toast";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import IconButton from "@mui/material/IconButton";
import { Collapse, TableBody, TableHead, Table, Box } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RefreshIcon from "@mui/icons-material/Refresh";

const Issuessummary = ({
  selectedRow,
  issueDetails,
  issuePartDetails,
  getIssuePartDetailsAPI,
  VehicleDetail,
  bookingDetail,
  handleImageOpen,
  issueGenericTransaction,
}) => {
  const issueType = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.issueType;
  delete issueType?.inverted;
  const issueStatus = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.issueStatus;
  const issuePriority = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.issuePriority;
  const fleetBookingStatus = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.fleetBookingStatus;
  let fuelTypeVehicle = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.fuelType;
  let transmissionType = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.transmissionType;
  const [open, setOpen] = useState(false);

  function normalizeAttachments(attachments) {
    if (!attachments) return [];

    // if already array
    if (Array.isArray(attachments)) return attachments;

    // if JSON string
    if (typeof attachments === "string") {
      try {
        const parsed = JSON.parse(attachments);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        return [];
      }
    }

    // if single object
    if (typeof attachments === "object") {
      return [attachments];
    }

    return [];
  }

  function createData(
    partname,
    partid,
    inventorypart,
    task,
    labourcharges,
    repairtype,
    tax,
    totalcost,
    attachments,
  ) {
    return {
      partname,
      partid,
      inventorypart,
      task,
      labourcharges,
      repairtype,
      tax,
      totalcost,
      attachments,
    };
  }

  const row =
    (issuePartDetails &&
      issuePartDetails?.map((item) =>
        createData(
          item?.partName,
          item?.partId,
          item?.isInventoryPart,
          item?.task || "/",
          item?.labourCharges || "/",
          item?.repairType,
          item?.taxAmount,
          item?.totalCost,
          normalizeAttachments(item?.attachments),
        ),
      )) ||
    [];

  return (
    <div>
      <div
        className={`${
          bookingDetail
            ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4"
            : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
        } gap-6 p-4 md:p-6`}
      >
        <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-2xl shadow-lg p-6 hover:scale-105 transition-transform duration-300">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-1 h-5 bg-indigo-600 rounded-full" />
            <h3 className="text-lg font-bold text-gray-800">Issue Details</h3>
          </div>

          <div className="space-y-1 text-gray-700 text-sm">
            {[
              { label: "Issue ID", value: issueDetails?.code },
              { label: "Reporter Name", value: issueDetails?.reporterName },
              { label: "Reporter Email", value: issueDetails?.reporterEmail },
              { label: "Vehicle", value: issueDetails?.vehicleName },
              {
                label: "Registration No",
                value: issueDetails?.vehicleRegistration,
              },
              {
                label: "Fuel Type",
                value: fuelTypeVehicle[VehicleDetail?.fuelType],
              },
              {
                label: "Transmission Type",
                value: transmissionType[VehicleDetail?.transmissionType],
              },
              { label: "Summary", value: issueDetails?.summary },
            ].map((item, i) => (
              <div
                key={i}
                className="flex justify-between items-center rounded-lg p-2 shadow-sm"
              >
                <span className="font-medium text-black">{item.label}</span>
                <span className="text-black">{item.value || "N/A"}</span>
              </div>
            ))}

            <div className="flex justify-between items-center rounded-lg p-2 shadow-sm mt-2">
              <span className="font-medium">Reported On</span>
              <span>
                {formatDateAndTime(issueDetails?.reportedOn) || "N/A"}
              </span>
            </div>

            <div className="flex justify-between items-center rounded-lg p-2 shadow-sm mt-2">
              <span className="font-medium">QC Status</span>
              <span
                className={`px-3 py-1 text-xs font-semibold rounded-full ${
                  issueDetails?.qcStatus === "Approved"
                    ? "bg-green-100 text-green-700"
                    : issueDetails?.qcStatus === "Pending"
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-red-100 text-red-700"
                }`}
              >
                {issueDetails?.qcStatus || "N/A"}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl shadow-lg p-6 hover:scale-105 transition-transform duration-300">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-1 h-5 bg-indigo-600 rounded-full" />
            <h3 className="text-lg font-bold text-gray-800">
              Additional Information
            </h3>
          </div>

          <div className="space-y-1 text-black text-sm">
            {[
              { label: "Reporter Type", value: issueDetails?.reporterType },
              {
                label: "Odometer Reading",
                value: issueDetails?.reportingOdometerReading
                  ? `${issueDetails.reportingOdometerReading} Km`
                  : "N/A",
              },
              {
                label: "Issue Status",
                value: issueStatus[issueDetails?.status] || "N/A",
              },
              {
                label: "Issue Type",
                value: issueType[issueDetails?.type] || "N/A",
              },
              {
                label: "Priority",
                value: issuePriority[issueDetails?.priority] || "N/A",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex justify-between items-center rounded-lg p-2 shadow-sm"
              >
                <span className="font-medium">{item.label}</span>
                <span className="text-gray-900">{item.value || "N/A"}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-2xl shadow-lg p-6 hover:scale-105 transition-transform duration-300">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-1 h-5 bg-indigo-600 rounded-full" />
            <h3 className="text-lg font-bold text-gray-800">Amount Details</h3>
          </div>

          <div className="space-y-1 text-black text-sm">
            {[
              { label: "Total Amount", value: issueDetails?.totalCost },
              {
                label: "Cost to Company",
                value: issueDetails?.costToTheCompany,
              },
              {
                label: "Cost to Customer",
                value: issueDetails?.costToTheCustomer,
              },
              { label: "Amount Claimed", value: issueDetails?.amountClaimed },
              { label: "Amount Received", value: issueDetails?.amountReceived },
              {
                label: "Total Paid",
                value: issueGenericTransaction && issueGenericTransaction?.reduce(
                  (accumulator, currentProduct) =>
                    accumulator + currentProduct.amount,
                  0,
                ),
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex justify-between items-center rounded-lg p-2 shadow-sm"
              >
                <span className="font-medium">{item.label}</span>
                <span className="text-gray-900 font-semibold">
                  ₹ {item.value || 0}
                </span>
              </div>
            ))}
            

            {/* {issueDetails?.costConfigAttachments &&
              (() => {
                let attachments = [];

                try {
                  attachments =
                    typeof issueDetails.costConfigAttachments === "string"
                      ? JSON.parse(issueDetails.costConfigAttachments)
                      : issueDetails.costConfigAttachments;
                } catch (e) {
                  console.error("Invalid costConfigAttachments", e);
                  return null;
                }

                if (!attachments?.length) return null;

                return (
                  <div className="flex justify-between items-center gap-3 pt-2">
                    <span className="font-medium text-black">
                      Cost Configuration Attachments
                    </span>

                    <img
                      src={attachments[0]?.url || attachments[0]?.path}
                      alt="Attachment"
                      onClick={() => {
                        handleImageOpen(
                          attachments[0]?.url || attachments[0]?.path
                        );
                      }}
                      className="w-16 h-16 object-cover rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
                    />
                  </div>
                );
              })()} */}
          </div>
        </div>

        {bookingDetail && (
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl shadow-lg p-6 hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-5 bg-indigo-600 rounded-full" />
              <h3 className="text-lg font-bold text-gray-800">
                Booking Details
              </h3>
            </div>

            <div className="space-y-1 text-black text-sm">
              {[
                { label: "Booking Code", value: bookingDetail[0]?.bookingCode },
                {
                  label: "Booking Status",
                  value: fleetBookingStatus[bookingDetail[0]?.bookingStatus],
                },
                {
                  label: "Start Date",
                  value: formatDateAndTime(bookingDetail[0]?.bookFrom),
                },
                {
                  label: "End date",
                  value: formatDateAndTime(bookingDetail[0]?.bookTo),
                },
                {
                  label: "Total days",
                  value: bookingDetail[0]?.totalNoOfDays + "/ days",
                },
                {
                  label: "Customer Name",
                  value: bookingDetail[0]?.customerName,
                },
                {
                  label: "Customer Email",
                  value: bookingDetail[0]?.customerEmail,
                },
                {
                  label: "Customer Mobile",
                  value: bookingDetail[0]?.customerMobile,
                },
                {
                  label: "Total Amount",
                  value: "₹" + bookingDetail[0]?.costToCustomer,
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center rounded-lg p-2 shadow-sm"
                >
                  <span className="font-medium">{item.label}</span>
                  <span className="text-gray-900 font-semibold">
                    {item.value || 0}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mx-6 mt-2">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-5 shadow-sm">
          {/* Header */}
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-5 bg-indigo-600 rounded-full" />
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
              Description
            </h3>
          </div>

          {/* Content */}
          <div
            className="text-gray-700 text-sm leading-relaxed max-h-60 overflow-y-auto pr-2 custom-scrollbar"
            dangerouslySetInnerHTML={{
              __html: issueDetails?.description || "No description provided.",
            }}
          />
        </div>
      </div>

      <div className="p-4">
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            className="shadow-lg rounded-lg"
          >
            <div className="flex items-center w-full">
              <Typography component="span">Part Details</Typography>

              <div className="ml-auto mr-10">
                <RefreshIcon
                  onClick={() => {
                    getIssuePartDetailsAPI(selectedRow?.id);
                  }}
                />
              </div>
            </div>
          </AccordionSummary>

          <AccordionDetails>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Attachments</TableCell>
                  <TableCell>Part Name</TableCell>
                  <TableCell align="right">Part ID</TableCell>
                  <TableCell align="right">Inventory</TableCell>
                  <TableCell align="right">Task</TableCell>
                  <TableCell align="right">Labour</TableCell>
                  <TableCell align="right">Repair Type</TableCell>
                  <TableCell align="right">Tax</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>

              <TableBody>
                {row.map((r, index) => (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => setOpen(open === index ? -1 : index)}
                        >
                          {open === index ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{r.partname}</TableCell>
                      <TableCell align="right">{r.partid}</TableCell>
                      <TableCell align="right">
                        {r.inventorypart ? "Yes" : "No"}
                      </TableCell>
                      <TableCell align="right">{r.task}</TableCell>
                      <TableCell align="right">{r.labourcharges}</TableCell>
                      <TableCell align="right">{r.repairtype}</TableCell>
                      <TableCell align="right">{r.tax}</TableCell>
                      <TableCell align="right">{r.totalcost}</TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 0 }}>
                        <Collapse in={open === index}>
                          <Box sx={{ m: 2, display: "flex", gap: 4 }}>
                            {r.attachments?.length > 0 ? (
                              r.attachments.map((file, i) => (
                                <div key={i}>
                                  <img
                                    src={
                                      file?.url ||
                                      file?.Location ||
                                      file?.image ||
                                      file?.path ||
                                      `Attachment ${i + 1}`
                                    }
                                    onClick={() => {
                                      handleImageOpen(
                                        file?.url ||
                                          file?.Location ||
                                          file?.image ||
                                          file?.path,
                                      );
                                    }}
                                    className="w-44 h-44 cursor-pointer"
                                  />
                                </div>
                              ))
                            ) : (
                              <Typography sx={{ color: "red" }} variant="body2">
                                No Attachments
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
};

// Example usage
export default Issuessummary;
